// SME Capture Flow — Phase 3 problems panel (cross-cutting).
//
// Available from any phase. Shows the problem areas the system has recorded —
// flagged by the expert, captured from "I don't know / it depends", or raised
// automatically when experts disagree — and lets the facilitator flag new ones
// or mark them resolved. Problems are never deleted, only resolved.

import React, { useState } from 'react';
import { CaptureProblem, ProblemKind } from '../hooks/useCapture';
import '../styles/CaptureProblems.css';

interface Props {
  problems: CaptureProblem[];
  // Resolves to whether the flag landed — the form keeps the typed text on
  // failure instead of silently discarding it.
  onFlag: (text: string, kind?: ProblemKind) => Promise<boolean>;
  onResolve: (id: string) => void;
}

const SOURCE_LABEL: Record<string, string> = {
  flagged: 'flagged',
  'dont-know': "don't know",
  'cross-track': 'experts disagree',
};

const CaptureProblems: React.FC<Props> = ({ problems, onFlag, onResolve }) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);

  const open_count = problems.filter((p) => !p.resolved).length;

  const submit = async () => {
    const t = text.trim();
    if (!t || sending) return;
    setSending(true);
    const ok = await onFlag(t, 'uncertain');
    setSending(false);
    // Only discard the typed text once it actually landed.
    if (ok) {
      setText('');
      setOpen(false);
    }
  };

  return (
    <section className="capture-problems">
      <div className="capture-problems-head">
        <h2>
          Problems
          {open_count > 0 && <span className="capture-problems-count">{open_count}</span>}
        </h2>
        <button className="capture-flag-btn" onClick={() => setOpen((v) => !v)}>
          ⚑ Flag a problem
        </button>
      </div>

      {open && (
        <div className="capture-flag-form">
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="What's broken, unclear, or disputed?"
            onKeyDown={(e) => {
              if (e.key === 'Enter') submit();
            }}
            autoFocus
          />
          <button onClick={submit} disabled={!text.trim() || sending}>
            {sending ? 'Adding…' : 'Add'}
          </button>
        </div>
      )}

      {problems.length === 0 ? (
        <p className="capture-problems-empty">
          No problems yet. Flag anything that&apos;s broken, uncertain, or &quot;it depends&quot;.
        </p>
      ) : (
        <ul className="capture-problems-list">
          {problems.map((p) => (
            <li key={p.id} className={`capture-problem${p.resolved ? ' resolved' : ''}`}>
              <span className={`capture-problem-tag tag-${p.source}`}>{SOURCE_LABEL[p.source] || p.source}</span>
              <span className="capture-problem-text">{p.text}</span>
              {p.resolved ? (
                <span className="capture-problem-done">resolved</span>
              ) : (
                <button className="capture-problem-resolve" onClick={() => onResolve(p.id)}>
                  Resolve
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default CaptureProblems;
